import { AnyAction } from 'redux'
import {
  startLoading,
  stopLoading,
  LoadingTypes,
  isLoading,
} from '@labrav/common'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import {
  BulkUploadSuccessMessage,
  DefaultSuccesrOnSubmitData,
  RequestFailureMessage,
  SingleScoreSuccessMessage,
} from '../../../common/utils/messagesContants'
import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import { getErrorMessages } from '../../../common/utils/error'
import {
  Chat,
  ChatCTA,
  CompletedStage,
  Conversation,
  ConversationResponse,
  FiltersState,
  FilterType,
  InferenceLimitUpdate,
  Limit,
  OnboardingStatusResponse,
  Recommendations,
  PropensityPayload,
  PropensityRow,
  SellerUrlUpdate,
  UserConfiguration,
  ActiveBatchProgress,
  PtbProgressSummary,
  PtbRowFeedbackEntry,
  PtbRowFeedbackVote,
  PtbSummaryActiveBatchProgressRaw,
  PtbSummaryStatusCount,
} from './reducer'
import { actionTypeWrapper } from '../../../common/utils/actionTypeWrapper'
import type { AppDispatch, RootState } from '../../../store'
import { snakeCase } from 'lodash'
import { errorLogger } from '../../../common/utils/errorLogger'
import {
  calculatePropensityScore,
  fetchPropensityData,
  fetchPtbRowFeedback,
  fetchStatus,
  getPreSignedUrlIntelligence,
  getInferenceLimit,
  postPtb,
  putInferenceLimit,
  submitPtbRowFeedback,
  updatePtbRowFeedbackComment,
  validateCsvFile,
  add_seller,
  sendPromptToAgent,
  fetchConversations,
  getChats,
  getRecommendations,
  checkOnboardingStatus,
  fetchAllIcps,
  updateIcpState,
  getUserConfigurations,
  putUserConfigurations,
  updateRecordArchivedStatus,
  retriggerPropensityCalculation,
} from '../../api'
import { fetchUsersList } from '../../../common/api/ptb'
import { createTypingSSEConnection } from '../../../common/utils/sseHelper'
import {
  normalizeStreamingResponse,
  StreamingEvent,
} from '../../../common/utils/responseNormalizer'
import { LastEvaluatedObject } from '@labrav/marketplace'
import { convertObjectKeysAndValuesToCommaSeprated } from '../../../common/utils/helperFunctions'
import { isEmpty } from 'lodash'
import { getSearchedData } from '../../../flyout/api/markeplace'
import FileDownload from 'js-file-download'
import { ICPData } from '../PartnerIQ/reducer'
import { setAllIcpData, updateIcpInList } from '../AllIcps/action'
import {
  ICP_FIT_LEVEL_QUERY_VALUES,
  IcpFitLevel,
} from '../../pages/components/Propensity/util/icpFitUtils'

let currentSSECancelFunction: (() => void) | null = null

export const cancelCurrentSSEConnection = () => {
  if (currentSSECancelFunction) {
    currentSSECancelFunction()
    currentSSECancelFunction = null
  }
}

// Helper function to create a StreamingEvent from an SSE event
const createStreamingEvent = (
  event: any,
  eventType?: string
): StreamingEvent => ({
  type: eventType || event.data?.params?.eventType || event.type || '',
  params: event.data?.params || {},
  data: event.data,
  timestamp: event.timestamp || event.data?.params?.timestamp,
})

// Helper function to handle stream completion
const handleStreamCompletion = (
  currentMessageId: string,
  collectedEvents: StreamingEvent[],
  getState: () => RootState,
  dispatch: AppDispatch
) => {
  const currentChat = getState().propensity.chats?.find(
    (chat: Chat) => chat.eventId === currentMessageId
  )
  const completedStages = currentChat?.completedStages || []
  const updates: any = {
    agentStage: 'complete',
    stageMessage: undefined,
  }

  if (currentChat?.error) {
    updates.error = currentChat.error
  }

  if (currentChat?.agentStage && currentChat?.stageMessage) {
    const isAlreadyCompleted = completedStages.some(
      (cs: CompletedStage) =>
        cs.stage === currentChat.agentStage &&
        cs.message === currentChat.stageMessage
    )
    if (!isAlreadyCompleted) {
      updates.completedStages = [
        ...completedStages,
        {
          stage: currentChat.agentStage,
          message: currentChat.stageMessage,
          timestamp: new Date().toISOString(),
        },
      ]
    }
  }

  try {
    const normalizedResponse = normalizeStreamingResponse(collectedEvents)
    if (normalizedResponse) {
      updates.content = normalizedResponse
    }
    dispatch(updateChat(currentMessageId, updates))
  } catch (error) {
    console.warn('Failed to normalize streaming response:', error)
    dispatch(updateChat(currentMessageId, updates))
  }
}

// Helper function to determine stage and message from event type
const getStageFromEventType = (
  eventType: string,
  event: any,
  currentChat: Chat | undefined
): {
  stage:
    | 'thinking'
    | 'retrieving'
    | 'using_tool'
    | 'processing'
    | 'responding'
    | 'complete'
    | undefined
  stageMessage: string | undefined
  shouldCompletePreviousStage: boolean
  previousStage:
    | 'thinking'
    | 'retrieving'
    | 'using_tool'
    | 'processing'
    | 'responding'
    | 'complete'
    | undefined
  previousStageMessage: string | undefined
} => {
  let stage:
    | 'thinking'
    | 'retrieving'
    | 'using_tool'
    | 'processing'
    | 'responding'
    | 'complete'
    | undefined
  let stageMessage: string | undefined
  let shouldCompletePreviousStage = false
  let previousStage:
    | 'thinking'
    | 'retrieving'
    | 'using_tool'
    | 'processing'
    | 'responding'
    | 'complete'
    | undefined
  let previousStageMessage: string | undefined

  if (eventType === 'server-tool-use') {
    if (currentChat?.agentStage && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'using_tool'
    const toolName = event.data?.params?.contentBlock?.content?.name || 'tool'
    const displayActivity =
      event.data?.params?.contentBlock?.content?.displayToolActivity
    stageMessage = displayActivity || `Using ${toolName}...`
  } else if (eventType === 'server-tool-response') {
    if (currentChat?.agentStage === 'using_tool' && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'processing'
    stageMessage = 'Processing results...'
  } else if (eventType === 'agent_handoff') {
    if (currentChat?.agentStage && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'thinking'
    stageMessage = 'Routing to specialist...'
  } else if (eventType === 'assistant-response.start') {
    if (currentChat?.agentStage && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'responding'
    stageMessage = 'Preparing response...'
  } else if (eventType === 'assistant-response.completed') {
    if (currentChat?.agentStage && currentChat?.stageMessage) {
      previousStage = currentChat.agentStage
      previousStageMessage = currentChat.stageMessage
      shouldCompletePreviousStage = true
    }
    stage = 'responding'
    stageMessage = 'Finalizing response...'
  }

  return {
    stage,
    stageMessage,
    shouldCompletePreviousStage,
    previousStage,
    previousStageMessage,
  }
}

// Helper function to handle stage updates
const handleStageUpdate = (
  eventType: string,
  event: any,
  currentMessageId: string | null,
  isStreamComplete: boolean,
  getState: () => RootState,
  dispatch: AppDispatch
) => {
  if (!currentMessageId) return

  const currentChat = getState().propensity.chats?.find(
    (chat: Chat) => chat.eventId === currentMessageId
  )

  const {
    stage,
    stageMessage,
    shouldCompletePreviousStage,
    previousStage,
    previousStageMessage,
  } = getStageFromEventType(eventType, event, currentChat)

  if (eventType === 'server-tool-response') {
    const toolResponseStatus = event.data?.params?.contentBlock?.content?.status
    const toolResponseOutput = event.data?.params?.contentBlock?.content?.output
    if (toolResponseStatus === 'error' && toolResponseOutput) {
      let errorMessage = ''
      try {
        if (typeof toolResponseOutput === 'string') {
          errorMessage = toolResponseOutput
        } else if (typeof toolResponseOutput === 'object') {
          errorMessage = JSON.stringify(toolResponseOutput, null, 2)
        }
      } catch (e) {
        errorMessage = String(toolResponseOutput)
      }

      if (errorMessage && currentMessageId) {
        dispatch(updateChat(currentMessageId, { error: errorMessage }))
      }
    }
  }

  if (stage && !isStreamComplete) {
    const completedStages = currentChat?.completedStages || []
    const updates: any = {
      agentStage: stage,
      stageMessage,
    }

    if (shouldCompletePreviousStage && previousStage && previousStageMessage) {
      const isAlreadyCompleted = completedStages.some(
        (cs: CompletedStage) =>
          cs.stage === previousStage && cs.message === previousStageMessage
      )
      if (!isAlreadyCompleted) {
        updates.completedStages = [
          ...completedStages,
          {
            stage: previousStage,
            message: previousStageMessage,
            timestamp: new Date().toISOString(),
          },
        ]
      }
    }

    dispatch(updateChat(currentMessageId, updates))
  }
}

// Helper function to handle CTA processing
const handleCTAProcessing = (
  event: any,
  currentMessageId: string | null,
  dispatch: AppDispatch
) => {
  if (!currentMessageId) return

  const rawCtas = Array.isArray(event.ctas)
    ? event.ctas
    : Array.isArray(event?.data?.ctas)
      ? event.data.ctas
      : []

  if (!rawCtas.length) return

  const normalizedCtas = rawCtas
    .map((cta: any): ChatCTA | null => {
      if (cta && typeof cta.label === 'string' && cta.prompt) {
        return {
          label: cta.label,
          prompt: cta.prompt,
        }
      }
      return null
    })
    .filter((cta: ChatCTA | null): cta is ChatCTA => Boolean(cta))

  if (normalizedCtas.length) {
    dispatch(
      updateChat(currentMessageId, {
        ctas: normalizedCtas,
      })
    )
  }
}

const calculateEffectiveTotal = (
  isEmptyPage: boolean,
  page: number,
  total: number | undefined,
  newCount: number,
  currentCount: number
): number => {
  if (isEmptyPage) return page <= 1 ? 0 : currentCount
  const calculated = page <= 1 ? newCount : currentCount + newCount
  return Math.max(total ?? 0, calculated)
}

export enum PropensityActionTypes {
  SET_REPORTS_DATA = 'SET_REPORTS_DATA',
  PROPENSITY_SET_PAGE_NUMBER = 'PROPENSITY_SET_PAGE_NUMBER',
  PROPENSITY_SET_LIMIT = 'PROPENSITY_SET_LIMIT',
  SET_PRODUCT_ID = 'SET_PRODUCT_ID',
  SET_DOMAIN_URLS = 'SET_DOMAIN_URLS',
  CLEAR_DOMAIN_URLS = 'CLEAR_DOMAIN_URLS',
  CLEAR_PROPENSITY_REPORTS = 'CLEAR_PROPENSITY_REPORTS',
  SET_FILTERS = 'SET_FILTERS',
  CLEAR_PROPENSITY_FILTERS = 'CLEAR_PROPENSITY_FILTERS',
  SET_DROP_DOWN_KEY = 'SET_DROP_DOWN_KEY',
  CLEAR_DROP_DOWN_KEY = 'CLEAR_DROP_DOWN_KEY',
  SHOW_DEFAULT = 'SHOW_DEFAULT',
  HIDE_DEFAULT = 'HIDE_DEFAULT',
  SHOW_ALERT = 'SHOW_ALERT',
  HIDE_ALERT = 'HIDE_ALERT',
  SET_INFERENCE_LIMIT = 'SET_INFERENCE_LIMIT',
  SET_SELLER = 'SET_SELLER',
  SET_PROMPT_INPUT = 'SET_PROMPT_INPUT',
  CLEAR_PROMPT_INPUT = 'CLEAR_PROMPT_INPUT',
  SET_CHATS = 'SET_CHATS',
  ADD_CHAT = 'ADD_CHAT',
  UPDATE_CHAT = 'UPDATE_CHAT',
  CLEAR_CHATS = 'CLEAR_CHATS',
  SET_ACTIVE_SESSION_ID = 'SET_ACTIVE_SESSION_ID',
  CLEAR_ACTIVE_SESSION_ID = 'CLEAR_ACTIVE_SESSION_ID',
  SET_CONVERSATIONS = 'SET_CONVERSATIONS',
  APPEND_CONVERSATIONS = 'APPEND_CONVERSATIONS',
  ADD_CONVERSATION = 'ADD_CONVERSATION',
  UPDATE_CONVERSATION = 'UPDATE_CONVERSATION',
  CLEAR_CONVERSATIONS = 'CLEAR_CONVERSATIONS',
  GET_CONVERSATIONS = 'GET_CONVERSATIONS',
  SET_CONVERSATIONS_PAGE_INFO = 'SET_CONVERSATIONS_PAGE_INFO',
  SET_CHAT_NEXT_TOKEN = 'SET_CHAT_NEXT_TOKEN',
  CLEAR_CHAT_NEXT_TOKEN = 'CLEAR_CHAT_NEXT_TOKEN',
  PREPEND_CHATS = 'PREPEND_CHATS',
  SET_HAS_MORE_CHATS = 'SET_HAS_MORE_CHATS',
  SET_RECOMMENDATIONS = 'SET_RECOMMENDATIONS',
  CLEAR_RECOMMENDATIONS = 'CLEAR_RECOMMENDATIONS',
  SET_HAS_OPENED_AI_DRAWER = 'SET_HAS_OPENED_AI_DRAWER',
  SET_IS_EXTERNAL_LOADING = 'SET_IS_EXTERNAL_LOADING',
  CLEAR_IS_EXTERNAL_LOADING = 'CLEAR_IS_EXTERNAL_LOADING',
  SET_USER_CONFIGURATIONS = 'SET_USER_CONFIGURATIONS',
  SET_UPLOADED_BY_USER_MAILS = 'SET_UPLOADED_BY_USER_MAILS',
  SET_PLATFORM_USERS_FROM_PTB = 'SET_PLATFORM_USERS_FROM_PTB',
  SET_ARCHIVED_FLAG = 'SET_ARCHIVED_FLAG',
  SET_ACTIVE_BATCH_PROGRESS = 'SET_ACTIVE_BATCH_PROGRESS',
  CLEAR_ACTIVE_BATCH_PROGRESS = 'CLEAR_ACTIVE_BATCH_PROGRESS',
  SET_PTB_PROGRESS_RESYNCING = 'SET_PTB_PROGRESS_RESYNCING',
  SET_ROW_FEEDBACK_MAP = 'SET_ROW_FEEDBACK_MAP',
  UPSERT_ROW_FEEDBACK = 'UPSERT_ROW_FEEDBACK',
}

const PTB_ARCHIVE_FIELD_KEYS = [
  'archived',
  'is_archived',
  'isArchived',
  'isArchive',
  'archive',
] as const

/** ES hit / `_source` keys we strip when inferring a flat document body. */
const PTB_ES_HIT_METADATA_KEYS = new Set([
  '_index',
  '_id',
  '_score',
  '_type',
  '_source',
  'source',
  'fields',
  'highlight',
  'sort',
])

/** Coerce API / ES archive flags to boolean; unknown shapes leave as null (fall through). */
const coercePtbArchivedFlag = (v: unknown): boolean | null => {
  if (v === undefined || v === null) return null
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') {
    if (v === 1) return true
    if (v === 0) return false
    return null
  }
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    if (['true', '1', 'yes', 'y'].includes(s)) return true
    if (['false', '0', 'no', 'n', ''].includes(s)) return false
    return null
  }
  if (typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>
    if (o.M && typeof o.M === 'object' && !Array.isArray(o.M)) {
      const inner = archivedFlagsFromDoc(o.M as Record<string, unknown>)
      if (inner !== null) return inner
    }
    if ('BOOL' in o) return Boolean(o.BOOL)
    if (typeof o.S === 'string') return coercePtbArchivedFlag(o.S)
    if (typeof o.N === 'string' || typeof o.N === 'number') {
      const n = typeof o.N === 'number' ? o.N : Number(o.N)
      return Number.isFinite(n) ? coercePtbArchivedFlag(n) : null
    }
  }
  return null
}

/** Unwrap DynamoDB-style `{ M: { ... } }` nodes returned in PTB search `_source.history`. */
const unwrapPtbDynamoMap = (node: unknown): Record<string, unknown> | null => {
  if (!node || typeof node !== 'object' || Array.isArray(node)) return null
  const o = node as Record<string, unknown>
  if (o.M && typeof o.M === 'object' && !Array.isArray(o.M)) {
    return o.M as Record<string, unknown>
  }
  return o as Record<string, unknown>
}

const archivedFlagsFromDoc = (
  doc: Record<string, unknown> | null
): boolean | null => {
  if (!doc) return null
  for (const key of PTB_ARCHIVE_FIELD_KEYS) {
    const t = coercePtbArchivedFlag(doc[key])
    if (t !== null) return t
  }
  return null
}

/** Like `archivedFlagsFromDoc` but also inspects one Dynamo `{ M: { ... } }` level on any value. */
const archivedFlagsFromDocDeep = (
  doc: Record<string, unknown> | null
): boolean | null => {
  const shallow = archivedFlagsFromDoc(doc)
  if (shallow !== null) return shallow
  if (!doc) return null
  for (const v of Object.values(doc)) {
    if (v === null || typeof v !== 'object' || Array.isArray(v)) continue
    const unwrapped = unwrapPtbDynamoMap(v)
    if (!unwrapped || unwrapped === v) continue
    const nested = archivedFlagsFromDoc(unwrapped)
    if (nested !== null) return nested
  }
  return null
}

const ptbSearchRecordTimestampMs = (src: Record<string, unknown>): number => {
  const raw = src.timestamp
  if (raw === undefined || raw === null) return 0
  const n = new Date(String(raw)).getTime()
  return Number.isFinite(n) ? n : 0
}

/** Latest activity time for a hit: root `timestamp` and every `history[].M.timestamp`. */
const ptbSearchRecordEffectiveTimestampMs = (
  src: Record<string, unknown>
): number => {
  let maxTs = ptbSearchRecordTimestampMs(src)
  const hist = src.history
  if (!Array.isArray(hist)) return maxTs
  for (const item of hist) {
    const inner = unwrapPtbDynamoMap(item)
    if (!inner) continue
    const tsRaw = inner.timestamp
    if (tsRaw === undefined || tsRaw === null) continue
    const n = new Date(String(tsRaw)).getTime()
    if (Number.isFinite(n)) maxTs = Math.max(maxTs, n)
  }
  return maxTs
}

const ptbHitDomainKey = (
  src: Record<string, unknown>,
  fallbackIndex: number
): string => {
  const d = String(src.prospect ?? src.domain_name ?? src.domainName ?? '')
    .trim()
    .toLowerCase()
  return d || `__nodo_${fallbackIndex}`
}

const ptbSearchHitIdForTieBreak = (src: Record<string, unknown>): string =>
  String(src._esHitId ?? src.id ?? '')

/**
 * Search returns one row per inference batch; multiple hits can share the same domain.
 * Keep the hit with the greatest effective timestamp; tie-break with ES hit id so sort
 * order from the API cannot surface an older batch when a newer archived row exists.
 */
const collapsePtbSearchHitsToLatestPerDomain = (
  hits: Record<string, unknown>[]
): Record<string, unknown>[] => {
  const byKey = new Map<string, Record<string, unknown>>()
  hits.forEach((hit, i) => {
    const key = ptbHitDomainKey(hit, i)
    const prev = byKey.get(key)
    if (!prev) {
      byKey.set(key, hit)
      return
    }
    const tHit = ptbSearchRecordEffectiveTimestampMs(hit)
    const tPrev = ptbSearchRecordEffectiveTimestampMs(prev)
    if (tHit > tPrev) {
      byKey.set(key, hit)
      return
    }
    if (tHit < tPrev) return
    if (ptbSearchHitIdForTieBreak(hit) > ptbSearchHitIdForTieBreak(prev)) {
      byKey.set(key, hit)
    }
  })
  return Array.from(byKey.values())
}

const MAX_PTB_SEARCH_PAGES = 40

const extractRawRecordsFromMarketplaceSearchResponse = (
  response: AxiosResponse | { data?: unknown }
): unknown[] => {
  const data = response?.data as
    | { records?: unknown; hits?: unknown }
    | undefined
  if (!data) return []
  if (Array.isArray(data.records)) return data.records as unknown[]
  if (Array.isArray(data.hits)) return data.hits as unknown[]
  return []
}

/** Follow pagination until all hits are loaded (or cap), so per-domain collapse sees every batch. */
const fetchAllPtbSearchHitsForPartner = async (params: {
  partnerId: string
  input?: string
  selectedInput?: string
  pageSize: number
  pageNumber?: number
  sectionName?: string
}): Promise<unknown[]> => {
  const {
    partnerId,
    input,
    selectedInput,
    pageSize,
    pageNumber: initialPage,
    sectionName,
  } = params
  const merged: unknown[] = []
  const seenHitIds = new Set<string>()
  let page = initialPage ?? 1
  let guard = 0

  while (guard++ < MAX_PTB_SEARCH_PAGES) {
    const response = await getSearchedData({
      partnerId,
      input,
      selectedInput,
      pageSize,
      pageNumber: page,
      sectionName,
      isRevenueSearch: false,
    })
    const batch = extractRawRecordsFromMarketplaceSearchResponse(response)
    let addedNewHits = 0
    for (const hit of batch) {
      const h = hit as { _id?: string }
      const hid = typeof h?._id === 'string' ? h._id : ''
      if (hid) {
        if (seenHitIds.has(hid)) continue
        seenHitIds.add(hid)
      }
      merged.push(hit)
      addedNewHits += 1
    }

    const body = response?.data as
      | {
          pagination_object?: {
            current_page?: number
            next_page?: number
          }
          total_count?: number
        }
      | undefined
    const po = body?.pagination_object
    const totalCount = Number(body?.total_count)
    const currentPage = Number(po?.current_page ?? page)
    const nextPage = po?.next_page

    const haveAllKnown =
      Number.isFinite(totalCount) &&
      totalCount > 0 &&
      merged.length >= totalCount

    if (batch.length === 0 || haveAllKnown) break
    if (addedNewHits === 0) break

    if (
      nextPage === undefined ||
      nextPage === null ||
      (typeof nextPage === 'number' &&
        (nextPage <= 0 || nextPage <= currentPage))
    ) {
      break
    }

    page = typeof nextPage === 'number' ? nextPage : page + 1
  }

  return merged
}

/**
 * PTB search `_source` often includes `history`: an array of snapshots (newest by `timestamp`
 * when timestamps exist). Used together with root `_source` in `isPtbSourceArchived`.
 */
const getLatestPtbHistorySnapshot = (
  src: any
): Record<string, unknown> | null => {
  if (
    !src?.history ||
    !Array.isArray(src.history) ||
    src.history.length === 0
  ) {
    return null
  }
  const scored: { ts: number; doc: Record<string, unknown> }[] = []
  for (const item of src.history) {
    const inner = unwrapPtbDynamoMap(item)
    if (!inner) continue
    const tsRaw = inner.timestamp
    const tsNum =
      tsRaw !== undefined && tsRaw !== null
        ? new Date(String(tsRaw)).getTime()
        : NaN
    if (Number.isFinite(tsNum)) {
      scored.push({ ts: tsNum, doc: inner })
    }
  }
  if (scored.length > 0) {
    scored.sort((a, b) => b.ts - a.ts)
    return scored[0].doc
  }
  const fallback = unwrapPtbDynamoMap(src.history[src.history.length - 1])
  return fallback
}

/**
 * Whether a PTB search hit is archived. If **either** root `_source` or the latest history
 * snapshot has an explicit `true`, treat as archived (archive is sticky). Otherwise use
 * explicit `false` from root, then history. This avoids: (1) stale history hiding a
 * freshly archived doc, (2) stale root hiding archive state only present in history.
 */
const isPtbSourceArchived = (src: any): boolean => {
  if (!src || typeof src !== 'object') return false
  const rootArchived = archivedFlagsFromDocDeep(src as Record<string, unknown>)
  if (rootArchived === true) return true
  const historyArchived = archivedFlagsFromDocDeep(
    getLatestPtbHistorySnapshot(src)
  )
  if (historyArchived === true) return true
  if (rootArchived === false) return false
  if (historyArchived === false) return false
  return false
}

/**
 * Merge Elasticsearch hit `_source` with top-level hit fields (archive flags are sometimes
 * only on the hit, not inside `_source`). `_source` wins when both define an archive key so
 * bad hit-level defaults cannot clear a correct `_source.archived`.
 */
const normalizePtbSearchHit = (record: any): any => {
  if (!record || typeof record !== 'object') return {}
  let sourceRaw: Record<string, unknown> =
    record._source && typeof record._source === 'object'
      ? (record._source as Record<string, unknown>)
      : {}
  if (
    Object.keys(sourceRaw).length === 0 &&
    record.source &&
    typeof record.source === 'object'
  ) {
    sourceRaw = record.source as Record<string, unknown>
  }
  if (Object.keys(sourceRaw).length === 0) {
    const flat: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(record)) {
      if (!PTB_ES_HIT_METADATA_KEYS.has(k)) flat[k] = v
    }
    if (Object.keys(flat).length) sourceRaw = flat
  }
  const src: Record<string, unknown> = { ...sourceRaw }
  for (const key of PTB_ARCHIVE_FIELD_KEYS) {
    if (
      key in record &&
      (record as Record<string, unknown>)[key] !== undefined &&
      !(key in sourceRaw && sourceRaw[key] !== undefined)
    ) {
      src[key] = (record as Record<string, unknown>)[key]
    }
  }
  if (typeof (record as { _id?: unknown })._id === 'string') {
    src._esHitId = (record as { _id: string })._id
  }
  return src
}

const ptbSearchSourceMatchesArchivedView = (
  src: any,
  viewArchived: boolean | undefined
): boolean => {
  if (viewArchived === undefined) return true
  const recArchived = isPtbSourceArchived(src)
  return viewArchived ? recArchived : !recArchived
}

const formatLastEvaluatedObject = (
  obj: LastEvaluatedObject | null | undefined
): LastEvaluatedObject | undefined => {
  if (!obj) return undefined
  const snakeized = snakeize(obj) as Record<string, string>
  if (
    'last_evaluated_key' in snakeized &&
    !('last_evaluated_keys' in snakeized)
  ) {
    const { last_evaluated_key, ...rest } = snakeized
    return { ...rest, last_evaluated_keys: last_evaluated_key }
  }
  return snakeized
}

const handlePropensityFetch = async ({
  partnerId,
  pageSize,
  lastEvaluatedObject,
  pageNumber,
  sectionName,
  selectedInput,
  input,
  filters,
  userMails,
  archived,
}: {
  partnerId: string
  pageSize: number
  lastEvaluatedObject?: LastEvaluatedObject | null
  selectedInput?: string
  input?: string
  pageNumber?: number
  sectionName?: string
  filters?: FilterType
  userMails?: string[]
  archived?: boolean
}) => {
  const formattedLastEvaluatedObject =
    formatLastEvaluatedObject(lastEvaluatedObject)
  if (isEmpty(input)) {
    const { data } = await fetchPropensityData(
      false,
      partnerId,
      pageSize,
      formattedLastEvaluatedObject,
      filters,
      userMails,
      archived
    )
    return data
  } else {
    const rawRecords = await fetchAllPtbSearchHitsForPartner({
      partnerId,
      input,
      selectedInput,
      pageSize,
      pageNumber,
      sectionName,
    })

    const normalizedHits = (rawRecords as any[]).map((record: any) =>
      normalizePtbSearchHit(record)
    )
    const dedupedByDomain =
      collapsePtbSearchHitsToLatestPerDomain(normalizedHits)
    const sortedSources = dedupedByDomain.sort(
      (a: any, b: any) =>
        ptbSearchRecordEffectiveTimestampMs(b as Record<string, unknown>) -
        ptbSearchRecordEffectiveTimestampMs(a as Record<string, unknown>)
    )

    const latestRecord = sortedSources.find((src: any) =>
      ptbSearchSourceMatchesArchivedView(src, archived)
    )

    if (latestRecord) {
      const domainName =
        latestRecord.domain_name ||
        latestRecord.domainName ||
        latestRecord.prospect ||
        ''
      latestRecord.id = `${partnerId}${domainName}`
      delete latestRecord._esHitId
    }

    const formattedResponse = {
      count: latestRecord ? 1 : 0,
      error: null,
      last_evaluated_object: null,
      records: latestRecord ? [latestRecord] : [],
    }
    return formattedResponse
  }
}

/** Normalize PTB summary API payload (snake_case / camelCase) at the boundary. */
export const normalizePtbProgressSummary = (
  raw: unknown
): PtbProgressSummary => {
  if (!raw || typeof raw !== 'object') return {}
  const data = raw as PtbProgressSummary
  return {
    logs_status_counts: data.logs_status_counts ?? data.logsStatusCounts,
    logsStatusCounts: data.logsStatusCounts ?? data.logs_status_counts,
    inference_status_counts:
      data.inference_status_counts ?? data.inferenceStatusCounts,
    inferenceStatusCounts:
      data.inferenceStatusCounts ?? data.inference_status_counts,
    active_batch_progress:
      data.active_batch_progress ?? data.activeBatchProgress,
    activeBatchProgress: data.activeBatchProgress ?? data.active_batch_progress,
  }
}

export const getSummary =
  (partnerId: string) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<PtbProgressSummary> => {
    try {
      const { data } = await fetchStatus(partnerId)
      return normalizePtbProgressSummary(data)
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    }
  }

type PollSummaryResult = {
  ready: boolean
  lastSummary: PtbProgressSummary | null
}

type ActivePollEntry = {
  promise: Promise<PollSummaryResult>
  abortController: AbortController
}

const activePollByPartner = new Map<string, ActivePollEntry>()

/** Abort and clear in-flight poll(s). Call on unmount / partnerId change. */
export const cancelActivePollSummary = (partnerId?: string): void => {
  if (partnerId) {
    const entry = activePollByPartner.get(partnerId)
    if (!entry) return
    entry.abortController.abort()
    activePollByPartner.delete(partnerId)
    return
  }
  for (const [id, entry] of activePollByPartner.entries()) {
    entry.abortController.abort()
    activePollByPartner.delete(id)
  }
}

const DOCUMENT_VISIBLE_MAX_WAIT_MS = 5 * 60 * 1000

/** In Vitest, never block on tab visibility — jsdom/CI can leave document.hidden sticky. */
const shouldIgnoreDocumentVisibility = (): boolean =>
  typeof process !== 'undefined' && typeof process.env?.VITEST !== 'undefined'

const waitUntilDocumentVisible = (signal?: AbortSignal): Promise<void> => {
  if (signal?.aborted) {
    return Promise.resolve()
  }
  if (
    shouldIgnoreDocumentVisibility() ||
    typeof document === 'undefined' ||
    !document.hidden
  ) {
    return Promise.resolve()
  }
  return new Promise(resolve => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      document.removeEventListener('visibilitychange', onVisible)
      signal?.removeEventListener('abort', onAbort)
      clearTimeout(maxWaitTimer)
      resolve()
    }
    const onVisible = () => {
      if (!document.hidden) finish()
    }
    const onAbort = () => finish()
    const maxWaitTimer = setTimeout(finish, DOCUMENT_VISIBLE_MAX_WAIT_MS)
    document.addEventListener('visibilitychange', onVisible)
    signal?.addEventListener('abort', onAbort)
  })
}

/** Poll delay that ends early when the tab becomes visible again (or on abort). */
const sleepForPollInterval = async (
  ms: number,
  signal?: AbortSignal
): Promise<void> => {
  if (signal?.aborted) {
    return
  }
  if (
    !shouldIgnoreDocumentVisibility() &&
    typeof document !== 'undefined' &&
    document.hidden
  ) {
    await waitUntilDocumentVisible(signal)
    return
  }

  await new Promise<void>(resolve => {
    let settled = false
    let timer: ReturnType<typeof setTimeout> | null = setTimeout(done, ms)
    let hiddenSafetyTimer: ReturnType<typeof setTimeout> | null = null
    let pausedWhileHidden = false

    function done() {
      if (settled) return
      settled = true
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange)
      }
      signal?.removeEventListener('abort', onAbort)
      if (timer != null) clearTimeout(timer)
      if (hiddenSafetyTimer != null) clearTimeout(hiddenSafetyTimer)
      resolve()
    }

    function onAbort() {
      done()
    }

    function onVisibilityChange() {
      if (shouldIgnoreDocumentVisibility()) return
      if (document.hidden && timer != null) {
        clearTimeout(timer)
        timer = null
        pausedWhileHidden = true
        // Avoid hanging forever if visibilitychange never fires again.
        if (hiddenSafetyTimer == null) {
          hiddenSafetyTimer = setTimeout(done, DOCUMENT_VISIBLE_MAX_WAIT_MS)
        }
      } else if (!document.hidden && pausedWhileHidden) {
        done()
      }
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange)
    }
    signal?.addEventListener('abort', onAbort)
  })
}

const runPollSummaryUntilReady =
  (
    partnerId: string,
    options?: {
      usePollSummaryLoader?: boolean
      useProgressVisibility?: boolean
      signal?: AbortSignal
    }
  ) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<PollSummaryResult> => {
    const useProgress = options?.useProgressVisibility === true
    const signal = options?.signal
    const loadingType = options?.usePollSummaryLoader
      ? LoadingTypes.PROPENSITY_POLL_SUMMARY
      : LoadingTypes.PROPENSITY_DATA
    await dispatch(startLoading(loadingType))
    try {
      const maxAttempts = useProgress ? 900 : 12
      const interval = useProgress ? 3000 : 15000
      let attempt = 0
      let lastSummary: PtbProgressSummary | null = null

      while (attempt < maxAttempts) {
        if (signal?.aborted) {
          if (useProgress) {
            dispatch(clearActiveBatchProgress())
          }
          return { ready: false, lastSummary }
        }

        await waitUntilDocumentVisible(signal)
        if (signal?.aborted) {
          if (useProgress) {
            dispatch(clearActiveBatchProgress())
          }
          return { ready: false, lastSummary }
        }

        if (attempt > 0) {
          await sleepForPollInterval(interval, signal)
          if (signal?.aborted) {
            if (useProgress) {
              dispatch(clearActiveBatchProgress())
            }
            return { ready: false, lastSummary }
          }
        }

        lastSummary = await dispatch(getSummary(partnerId))
        if (signal?.aborted) {
          if (useProgress) {
            dispatch(clearActiveBatchProgress())
          }
          return { ready: false, lastSummary }
        }

        if (useProgress) {
          const activeBatches = normalizeActiveBatchProgress(
            lastSummary?.active_batch_progress ??
              lastSummary?.activeBatchProgress
          )
          dispatch(setActiveBatchProgress(activeBatches))

          // Soft-refresh table so queued InferenceStore rows appear without clearing.
          const state = getState()
          const uploadedMails = state.propensity?.uploadedByUserMails || []
          await dispatch(
            getPropensityData({
              partnerId,
              skipLoading: true,
              hydrateFeedback: false,
              userMails: uploadedMails.length > 0 ? uploadedMails : undefined,
              archived: Boolean(state.propensity?.archived),
            })
          )

          const inferenceCounts =
            lastSummary?.inference_status_counts ??
            lastSummary?.inferenceStatusCounts
          if (Array.isArray(inferenceCounts)) {
            const inferenceInFlight = countInferenceStoreInFlight(lastSummary)
            if (inferenceInFlight === 0) {
              dispatch(clearActiveBatchProgress())
              return { ready: true, lastSummary }
            }
          } else {
            const inProgress =
              lastSummary?.logs_status_counts?.find(
                s => s.status === 'IN_PROGRESS'
              )?.count ?? 0
            if (inProgress === 0) {
              dispatch(clearActiveBatchProgress())
              return { ready: true, lastSummary }
            }
          }
        } else {
          const inProgress =
            lastSummary?.logs_status_counts?.find(
              s => s.status === 'IN_PROGRESS'
            )?.count ?? 0

          if (inProgress === 0) {
            return { ready: true, lastSummary }
          }
        }

        attempt++
      }

      if (useProgress) {
        dispatch(clearActiveBatchProgress())
      }
      return { ready: true, lastSummary }
    } finally {
      await dispatch(stopLoading(loadingType))
    }
  }

export const pollSummaryUntilReady =
  (
    partnerId: string,
    options?: {
      usePollSummaryLoader?: boolean
      useProgressVisibility?: boolean
    }
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const existing = activePollByPartner.get(partnerId)
    if (existing && !existing.abortController.signal.aborted) {
      return existing.promise
    }
    if (existing) {
      activePollByPartner.delete(partnerId)
    }

    const abortController = new AbortController()
    const pollPromise = dispatch(
      runPollSummaryUntilReady(partnerId, {
        ...options,
        signal: abortController.signal,
      })
    )
    activePollByPartner.set(partnerId, {
      promise: pollPromise,
      abortController,
    })
    try {
      return await pollPromise
    } finally {
      const current = activePollByPartner.get(partnerId)
      if (current?.promise === pollPromise) {
        activePollByPartner.delete(partnerId)
      }
    }
  }

/** Soft-refresh rows and resume polling after the browser tab becomes visible. */
export const refreshPtbProgressOnVisible =
  (
    partnerId: string,
    options?: {
      usePollSummaryLoader?: boolean
      useProgressVisibility?: boolean
    }
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (typeof document !== 'undefined' && document.hidden) {
      return
    }

    const state = getState()
    const pollAlreadyActive = isLoading(state.loading)(
      LoadingTypes.PROPENSITY_POLL_SUMMARY
    )
    const uploadedMails = state.propensity?.uploadedByUserMails || []
    const dataRefresh = dispatch(
      getPropensityData({
        partnerId,
        skipLoading: true,
        hydrateFeedback: false,
        userMails: uploadedMails.length > 0 ? uploadedMails : undefined,
        archived: Boolean(state.propensity?.archived),
      })
    )

    if (pollAlreadyActive) {
      await dataRefresh
      return
    }

    const useProgress = options?.useProgressVisibility === true
    let shouldPoll = false

    try {
      const [lastSummary] = await Promise.all([
        dispatch(getSummary(partnerId)),
        dataRefresh,
      ])
      const logsInProgress =
        lastSummary?.logs_status_counts?.find(s => s.status === 'IN_PROGRESS')
          ?.count ?? 0
      const inferenceInFlight = useProgress
        ? countInferenceStoreInFlight(lastSummary)
        : 0
      shouldPoll = logsInProgress > 0 || inferenceInFlight > 0
    } catch {
      await dataRefresh.catch(() => undefined)
      return
    }

    if (shouldPoll) {
      await dispatch(
        pollSummaryUntilReady(partnerId, {
          usePollSummaryLoader: options?.usePollSummaryLoader,
          useProgressVisibility: options?.useProgressVisibility,
        })
      )
    }
  }

export const setPtbProgressResyncing = (resyncing: boolean) => ({
  type: PropensityActionTypes.SET_PTB_PROGRESS_RESYNCING,
  payload: resyncing,
})

const IN_FLIGHT_STATUSES = new Set(['queued', 'researching', 'scoring'])

export const countInferenceStoreInFlight = (
  summary: PtbProgressSummary | null | undefined
): number => {
  const counts: PtbSummaryStatusCount[] =
    summary?.inference_status_counts ?? summary?.inferenceStatusCounts ?? []
  if (!Array.isArray(counts)) return 0
  return counts.reduce((sum, entry) => {
    const status = String(entry?.status || '').toLowerCase()
    if (!IN_FLIGHT_STATUSES.has(status)) return sum
    return sum + (Number(entry?.count) || 0)
  }, 0)
}

const normalizeActiveBatchProgress = (
  raw: PtbSummaryActiveBatchProgressRaw[] | undefined | null
): ActiveBatchProgress[] => {
  if (!Array.isArray(raw)) return []
  return raw
    .map(item => ({
      inferenceBatchId: String(
        item?.inference_batch_id ?? item?.inferenceBatchId ?? ''
      ),
      progressPct: Number(item?.progress_pct ?? item?.progressPct ?? 0),
      rowsTotal: Number(item?.rows_total ?? item?.rowsTotal ?? 0),
      rowsCompleted: Number(item?.rows_completed ?? item?.rowsCompleted ?? 0),
      rowsFailed: Number(item?.rows_failed ?? item?.rowsFailed ?? 0),
    }))
    .filter(item => item.inferenceBatchId)
}

export const setActiveBatchProgress = (batches: ActiveBatchProgress[]) => ({
  type: PropensityActionTypes.SET_ACTIVE_BATCH_PROGRESS,
  payload: batches,
})

export const clearActiveBatchProgress = () => ({
  type: PropensityActionTypes.CLEAR_ACTIVE_BATCH_PROGRESS,
})

export const setRowFeedbackMap = (
  map: Record<string, PtbRowFeedbackEntry>
) => ({
  type: PropensityActionTypes.SET_ROW_FEEDBACK_MAP,
  payload: map,
})

export const upsertRowFeedback = (
  inferenceId: string,
  feedback: PtbRowFeedbackEntry
) => ({
  type: PropensityActionTypes.UPSERT_ROW_FEEDBACK,
  payload: { inferenceId, feedback },
})

const resolvePtbFeedbackPartnerId = (
  partnerId: string | undefined,
  getState: () => RootState
) => partnerId || getState().AccountData.user.partnerData?.partnerId || ''

const resolvePtbFeedbackUserId = (getState: () => RootState) => {
  const user = getState().userProfile.userProfile
  return user?.id || user?.idpUserId || ''
}

export const fetchPtbFeedbackForRows =
  (partnerId: string, inferenceIds: string[], { merge = false } = {}) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const resolvedPartnerId = resolvePtbFeedbackPartnerId(partnerId, getState)
    const userId = resolvePtbFeedbackUserId(getState)
    if (!resolvedPartnerId || !userId || !inferenceIds.length) {
      return
    }
    try {
      const { data } = await fetchPtbRowFeedback(
        resolvedPartnerId,
        userId,
        inferenceIds
      )
      const records = (data?.records || []) as Array<{
        inferenceId?: string
        inference_id?: string
        vote?: string
        comment?: string | null
      }>
      const map: Record<string, PtbRowFeedbackEntry> = {}
      for (const rec of records) {
        const id = String(rec.inferenceId ?? rec.inference_id ?? '').trim()
        const vote = String(rec.vote || '').toLowerCase()
        if (!id || (vote !== 'up' && vote !== 'down')) continue
        map[id] = {
          vote: vote as PtbRowFeedbackVote,
          comment: rec.comment ?? null,
        }
      }
      if (merge) {
        dispatch(
          setRowFeedbackMap({
            ...(getState().propensity.rowFeedbackById || {}),
            ...map,
          })
        )
      } else {
        dispatch(setRowFeedbackMap(map))
      }
    } catch (error) {
      // Non-blocking: table still works without hydrated votes.
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    }
  }

export const submitPtbFeedback =
  ({
    partnerId,
    inferenceId,
    vote,
    domainName,
    customerName,
  }: {
    partnerId: string
    inferenceId: string
    vote: PtbRowFeedbackVote
    domainName?: string
    customerName?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const user = getState().userProfile.userProfile
    const resolvedPartnerId = resolvePtbFeedbackPartnerId(partnerId, getState)
    const userId = resolvePtbFeedbackUserId(getState)
    if (!resolvedPartnerId || !inferenceId || !userId) {
      dispatch(
        updateAppAlert({
          message: 'Unable to save feedback. Missing partner or user context.',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return
    }
    const firstName = user?.givenName || user?.firstName || ''
    const lastName = user?.lastName || user?.familyName || ''
    const userName = `${firstName} ${lastName}`.trim() || undefined
    const previous = getState().propensity.rowFeedbackById?.[inferenceId]
    dispatch(
      upsertRowFeedback(inferenceId, {
        vote,
        comment: vote === 'up' ? null : (previous?.comment ?? null),
      })
    )
    try {
      const { data } = await submitPtbRowFeedback(
        resolvedPartnerId,
        inferenceId,
        {
          vote,
          user_id: userId,
          domain_name: domainName,
          customer_name: customerName,
          user_mail: user?.email || undefined,
          user_name: userName,
        }
      )
      const camel = camelize(data || {}) as {
        vote?: string
        comment?: string | null
      }
      const nextVote = String(camel.vote || vote).toLowerCase()
      if (nextVote === 'up' || nextVote === 'down') {
        dispatch(
          upsertRowFeedback(inferenceId, {
            vote: nextVote as PtbRowFeedbackVote,
            comment: nextVote === 'up' ? null : (camel.comment ?? null),
          })
        )
      }
      return data
    } catch (error: any) {
      if (previous) {
        dispatch(upsertRowFeedback(inferenceId, previous))
      } else {
        const nextMap = { ...(getState().propensity.rowFeedbackById || {}) }
        delete nextMap[inferenceId]
        dispatch(setRowFeedbackMap(nextMap))
      }
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      throw error
    }
  }

export const updatePtbFeedbackComment =
  ({
    partnerId,
    inferenceId,
    comment,
  }: {
    partnerId: string
    inferenceId: string
    comment: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const resolvedPartnerId = resolvePtbFeedbackPartnerId(partnerId, getState)
    const userId = resolvePtbFeedbackUserId(getState)
    if (!resolvedPartnerId || !inferenceId || !userId) {
      dispatch(
        updateAppAlert({
          message:
            'Unable to save feedback comment. Missing partner or user context.',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return
    }
    const previous = getState().propensity.rowFeedbackById?.[inferenceId]
    dispatch(
      upsertRowFeedback(inferenceId, {
        vote: previous?.vote || 'down',
        comment,
      })
    )
    try {
      const { data } = await updatePtbRowFeedbackComment(
        resolvedPartnerId,
        inferenceId,
        {
          user_id: userId,
          comment,
        }
      )
      const camel = camelize(data || {}) as {
        vote?: string
        comment?: string | null
      }
      dispatch(
        upsertRowFeedback(inferenceId, {
          vote: (String(
            camel.vote || previous?.vote || 'down'
          ).toLowerCase() === 'up'
            ? 'up'
            : 'down') as PtbRowFeedbackVote,
          comment: camel.comment ?? comment,
        })
      )
      return data
    } catch (error: any) {
      if (previous) {
        dispatch(upsertRowFeedback(inferenceId, previous))
      }
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      throw error
    }
  }

export const getPropensityData =
  ({
    partnerId,
    downloadReport,
    pageSizeValue,
    pageNumber,
    lastEvaluatedObject,
    input,
    selectedInput,
    sectionName,
    userMails,
    archived,
    skipLoading,
    hydrateFeedback,
  }: {
    partnerId?: string
    downloadReport?: boolean
    pageSizeValue?: number
    pageNumber?: number
    lastEvaluatedObject?: LastEvaluatedObject | null
    input?: string
    selectedInput?: string
    sectionName?: string
    userMails?: string[]
    archived?: boolean
    /** Soft refresh during progress polling — skip opaque PROPENSITY_DATA loader */
    skipLoading?: boolean
    /**
     * When true, hydrate row feedback after fetch.
     * Progress soft-refreshes must omit this (or pass false) so feedback is not
     * re-fetched every poll tick. Supply true only when PTB improvements UI is on.
     */
    hydrateFeedback?: boolean
  } = {}) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!skipLoading) {
      await dispatch(startLoading(LoadingTypes.PROPENSITY_DATA))
    }

    try {
      const resolvedPartnerId =
        partnerId || getState().AccountData.user.partnerData?.partnerId || ''
      const { pageSize: pageSizeStateValue } = getState().propensity
      const pageSize = pageSizeValue ?? pageSizeStateValue
      const { filters } = getState().propensity

      const formatFilterValue = (value: string) =>
        value === 'All' ? 'all' : value

      const formatedFilters: FilterType = {
        time_range: filters?.lastUpdated || '',
        marketplaces:
          filters?.marketplace?.map(formatFilterValue).join(',') || '',
        propensity_level:
          filters?.propensityScore?.map(formatFilterValue).join(',') || '',
        aws_level: filters?.aws?.map(formatFilterValue).join(',') || '',
        azure_level: filters?.azure?.map(formatFilterValue).join(',') || '',
        gcp_level: filters?.gcp?.map(formatFilterValue).join(',') || '',
        icp_qualified: Array.isArray(filters?.icpQualified)
          ? filters.icpQualified.length
            ? filters.icpQualified
                .map((level: string) => {
                  const key = level as IcpFitLevel
                  return ICP_FIT_LEVEL_QUERY_VALUES[key] ?? level
                })
                .join(',')
            : undefined
          : (filters?.icpQualified ?? undefined),
        is_crm: filters?.isCrm ?? undefined,
        expected_revenue_min:
          filters?.expectedRevenueMin != null
            ? filters.expectedRevenueMin
            : undefined,
        expected_revenue_max:
          filters?.expectedRevenueMax != null
            ? filters.expectedRevenueMax
            : undefined,
      }

      if (!downloadReport) {
        const viewArchived =
          typeof archived === 'boolean'
            ? archived
            : Boolean(getState().propensity.archived)
        const data = await handlePropensityFetch({
          partnerId: resolvedPartnerId,
          pageSize,
          lastEvaluatedObject,
          selectedInput,
          input,
          pageNumber,
          sectionName,
          filters: formatedFilters,
          userMails,
          archived: viewArchived,
        })
        const effectivePageNumber = pageNumber ?? 1
        const isPtbPaginationFetch =
          sectionName === 'ptb' && effectivePageNumber > 1
        await dispatch(
          setReportsData(camelize(data), pageSize, isPtbPaginationFetch)
        )
        if (!isPtbPaginationFetch) {
          await dispatch(clearDomainUrls())
        }
        const records = (camelize(data) as { records?: Array<{ id?: string }> })
          ?.records
        const inferenceIds = (records || [])
          .map(r => String(r?.id || '').trim())
          .filter(Boolean)
        // Only hydrate when explicitly requested (PTB improvements UI). Never on
        // progress soft-refreshes (skipLoading / hydrateFeedback: false).
        if (inferenceIds.length > 0 && hydrateFeedback === true) {
          await dispatch(
            fetchPtbFeedbackForRows(resolvedPartnerId, inferenceIds, {
              merge: isPtbPaginationFetch,
            })
          )
        }
      } else {
        await downloadPtbReports({
          downloadReport,
          partnerId: resolvedPartnerId,
          pageSize,
          lastEvaluatedObject,
          pageNumber,
          sectionName,
          selectedInput,
          input,
          filters: formatedFilters,
          userMails,
        })
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (!skipLoading) {
        dispatch(stopLoading(LoadingTypes.PROPENSITY_DATA))
      }
    }
  }

export const downloadPtbReports = async ({
  downloadReport,
  partnerId,
  pageSize,
  lastEvaluatedObject,
  pageNumber,
  sectionName,
  selectedInput,
  input,
  filters,
  userMails,
}: {
  downloadReport?: boolean
  partnerId: string
  pageSize: number
  lastEvaluatedObject?: LastEvaluatedObject | null
  selectedInput?: string
  input?: string
  pageNumber?: number
  sectionName?: string
  filters?: FilterType
  userMails?: string[]
}) => {
  const formattedLastEvaluatedObject =
    formatLastEvaluatedObject(lastEvaluatedObject)
  if (isEmpty(input)) {
    return fetchPropensityData(
      downloadReport,
      partnerId,
      pageSize,
      formattedLastEvaluatedObject,
      filters,
      userMails,
      undefined
    ).then(response => {
      FileDownload(response.data, `${partnerId}.csv`)
    })
  } else {
    return getSearchedData({
      partnerId,
      input,
      selectedInput,
      pageSize,
      pageNumber,
      sectionName,
      downloadToCsv: true,
    }).then(response => {
      FileDownload(response.data, `${partnerId}.csv`)
    })
  }
}

export const validatePtbFileOnly =
  (partnerId: string, s3Path: string) =>
  async (dispatch: AppDispatch): Promise<AxiosResponse<any>> => {
    // This thunk only validates the uploaded PTB file and returns the raw API response.
    // UI components are responsible for handling success/error messaging.
    const response = await validateCsvFile(partnerId, s3Path)
    return response as unknown as AxiosResponse<any>
  }

export const validateAndPostPtb =
  (partnerId: string, s3Path: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_SCORE_ACTION))
    try {
      const response = await validateCsvFile(partnerId, s3Path)
      if (response.data.status === true) {
        const { data } = await postPtb(partnerId, s3Path)

        dispatch(
          updateAppAlert({
            message: BulkUploadSuccessMessage,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        return data
      } else if (response.data.status === false) {
        dispatch(
          updateAppAlert({
            message: response.data.message,
            messageType: 'ERROR',
            autoClose: false,
          })
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.PROPENSITY_SCORE_ACTION))
    }
  }

export const postPtbCall =
  (
    partnerId: string,
    s3Path: string,
    suppressSnackbarAlert = false,
    userId?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_SCORE_ACTION))
    try {
      const state = getState()
      const partnerCrmId = state.AccountData.user.partnerData?.crmId || null
      const currentUser = state.userProfile.userProfile || null
      const userIdToSend = userId || currentUser?.id || undefined
      const userMail = currentUser?.email || undefined

      const firstName = currentUser?.givenName || currentUser?.firstName || ''
      const lastName = currentUser?.lastName || ''
      const userName = `${firstName} ${lastName}`.trim()

      const { data } = await postPtb(
        partnerId,
        s3Path,
        userIdToSend,
        userMail,
        partnerCrmId,
        userName
      )

      if (!suppressSnackbarAlert) {
        dispatch(
          updateAppAlert({
            message: BulkUploadSuccessMessage,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      }

      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.PROPENSITY_SCORE_ACTION))
    }
  }

export const calculatePtbScore =
  (
    partnerId: string,
    selectedKeys: string[],
    userId?: string,
    options?: { signalRefresh?: boolean }
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_SCORE_ACTION))
    try {
      const state = getState()
      const { records } = state.propensity

      // selectedKeys may be either row ids or plain domain names (legacy).
      // Resolve each key to a domainName when possible, falling back to the key itself.
      const prospects = selectedKeys.map(key => {
        const match =
          records.find((r: PropensityRow) => r.id === key) ||
          records.find((r: PropensityRow) => r.domainName === key)
        return match?.domainName || key
      })
      const partnerCrmId = state.AccountData.user.partnerData?.crmId || null
      const crmFormData = state.CRMAuth?.crmFormData

      const isCrmConnected =
        !isEmpty(crmFormData) &&
        !isEmpty(partnerCrmId) &&
        (crmFormData?.creds || []).length > 0 &&
        isEmpty(crmFormData?.deleted_at)

      const crmIdToSend = isCrmConnected ? partnerCrmId : undefined
      const currentUser = state.userProfile.userProfile || null
      const userMail = currentUser?.email || undefined

      const firstName = currentUser?.givenName || currentUser?.firstName || ''
      const lastName = currentUser?.lastName || ''
      const userName = `${firstName} ${lastName}`.trim()

      const { data } = await calculatePropensityScore(
        partnerId,
        prospects.map(prospect => ({ prospect })),
        userId,
        crmIdToSend,
        userMail,
        userName,
        options?.signalRefresh
      )
      dispatch(
        updateAppAlert({
          message: SingleScoreSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.PROPENSITY_SCORE_ACTION))
    }
  }

export const retriggerPropensity =
  (partnerId: string, userId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_SCORE_ACTION))
    try {
      const state = getState()
      const partnerCrmId = state.AccountData.user.partnerData?.crmId || null
      const crmFormData = state.CRMAuth?.crmFormData

      const isCrmConnected =
        !isEmpty(crmFormData) &&
        !isEmpty(partnerCrmId) &&
        (crmFormData?.creds || []).length > 0 &&
        isEmpty(crmFormData?.deleted_at)

      const crmIdToSend = isCrmConnected ? partnerCrmId : undefined
      const currentUser = state.userProfile.userProfile || null
      const userMail = currentUser?.email || null
      const firstName = currentUser?.givenName || currentUser?.firstName || ''
      const lastName = currentUser?.lastName || ''
      const userName = `${firstName} ${lastName}`.trim()
      await retriggerPropensityCalculation(
        partnerId,
        userId,
        crmIdToSend,
        userMail,
        userName
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      updateAppAlert({
        message: getErrorMessages([RequestFailureMessage])(errorResponse),
        messageType: 'ERROR',
        autoClose: true,
      })

      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.PROPENSITY_SCORE_ACTION))
    }
  }

export const updateArchivedStatus =
  (
    partnerId: string,
    recordIds: string[],
    archived: boolean,
    userMails?: string[]
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.PROPENSITY_DATA))
    try {
      await updateRecordArchivedStatus(partnerId, recordIds, archived)
      await dispatch(fetchInferenceLimit(partnerId, userMails))
      dispatch(
        updateAppAlert({
          message: archived
            ? `${recordIds.length} account${recordIds.length > 1 ? 's' : ''} archived successfully`
            : `${recordIds.length} account${recordIds.length > 1 ? 's' : ''} unarchived successfully`,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.PROPENSITY_DATA))
    }
  }

export const getPreSignedUrl =
  (partnerId: string, fileKeys: string[], suppressSnackbarAlert = false) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))

    try {
      const { data } = await getPreSignedUrlIntelligence(partnerId, fileKeys)
      if (!suppressSnackbarAlert) {
        dispatch(
          updateAppAlert({
            message: DefaultSuccesrOnSubmitData,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      }
      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      if (!suppressSnackbarAlert) {
        dispatch(
          updateAppAlert({
            message: getErrorMessages([RequestFailureMessage])(errorResponse),
            messageType: 'ERROR',
            autoClose: true,
          })
        )
      }
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const getPropensityPreSignedUrl =
  (partnerId: string, path: string, suppressSnackbarAlert = false) =>
  async (dispatch: AppDispatch) => {
    try {
      const response = await dispatch(
        getPreSignedUrl(partnerId, [path], suppressSnackbarAlert)
      )
      return {
        data: [
          {
            presigned_url_data: response,
          },
        ],
      }
    } catch (error) {
      console.error('Error getting pre-signed URL:', error)
      return {
        data: [
          {
            presigned_url_data: {},
          },
        ],
      }
    }
  }

export const fetchInferenceLimit =
  (partnerId: string, userMails?: string[]) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_INFERENCE_LIMIT))
    try {
      const response = await getInferenceLimit(
        partnerId,
        userMails ? userMails : undefined
      )
      dispatch(setInferenceLimit(snakeize(response.data)))
      dispatch(setSeller(true))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      const message = errorResponse?.data?.errors?.[0] || ''

      if (message.toLowerCase().includes('sellers not found')) {
        dispatch(setSeller(false))
      }
      const globalState = getState()

      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_INFERENCE_LIMIT))
    }
  }

export const sendPromptToAIAgent =
  (
    partnerId: string,
    data: {
      prompt: string
      user_id: string
      session_id?: string
      conversation_title?: string
      agent_type?: string
    }
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const activeSessionId = getState().propensity.activeSessionId || ''
    await dispatch(startLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
    let loaderStopped = false

    try {
      if (currentSSECancelFunction) {
        currentSSECancelFunction()
        currentSSECancelFunction = null
      }

      const { url, body } = sendPromptToAgent(partnerId, data)
      let sessionId: string | null = null
      let currentMessageId: string | null = null
      let messageCreated = false
      const collectedEvents: StreamingEvent[] = []
      let isStreamComplete = false

      const connection = createTypingSSEConnection(
        { url, body },
        {
          onStart: event => {
            const eventData = createStreamingEvent(event)
            collectedEvents.push(eventData)

            sessionId =
              event.session_id ||
              event.data?.params?.sessionId ||
              event.data?.sessionId ||
              null

            if (sessionId && sessionId !== activeSessionId) {
              dispatch(setActiveSessionId(sessionId))
            }

            const eventType = event.data?.params?.eventType || event.type || ''
            const isStartEvent =
              eventType === 'stream_start' ||
              eventType === 'assistant-response.start' ||
              eventType === 'start'

            if (isStartEvent && !messageCreated) {
              currentMessageId = Date.now().toString()
              messageCreated = true

              dispatch(
                addChat({
                  eventId: currentMessageId,
                  content: '',
                  role: 'AGENT_RESPONSE',
                  timestamp:
                    event.timestamp ||
                    event.data?.params?.timestamp ||
                    new Date().toISOString(),
                  agentStage: 'thinking',
                  stageMessage: 'Initializing...',
                })
              )
            }
          },
          onContent: event => {
            const eventData = createStreamingEvent(
              event,
              'assistant-response.delta'
            )
            collectedEvents.push(eventData)
          },
          onTyping: text => {
            if (currentMessageId) {
              dispatch(updateChat(currentMessageId, { content: text }))
            }
          },
          onTypingComplete: finalText => {
            if (currentMessageId && collectedEvents.length > 0) {
              try {
                const normalizedResponse =
                  normalizeStreamingResponse(collectedEvents)
                if (normalizedResponse) {
                  dispatch(
                    updateChat(currentMessageId, {
                      content: normalizedResponse,
                    })
                  )
                } else if (finalText) {
                  dispatch(updateChat(currentMessageId, { content: finalText }))
                }
              } catch (error) {
                if (finalText) {
                  dispatch(updateChat(currentMessageId, { content: finalText }))
                }
              }
            }
          },
          onCustom: (eventType, event) => {
            const eventData = createStreamingEvent(event, eventType)
            collectedEvents.push(eventData)

            if (
              (eventType === 'stream_end' || eventType === 'end') &&
              currentMessageId &&
              !isStreamComplete
            ) {
              isStreamComplete = true

              if (!loaderStopped) {
                dispatch(stopLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
                loaderStopped = true
              }

              handleStreamCompletion(
                currentMessageId,
                collectedEvents,
                getState,
                dispatch
              )
            }

            handleStageUpdate(
              eventType,
              event,
              currentMessageId,
              isStreamComplete,
              getState,
              dispatch
            )

            if (eventType === 'cta') {
              handleCTAProcessing(event, currentMessageId, dispatch)
            }
          },
          onEnd: event => {
            if (event) {
              const eventData = createStreamingEvent(event, 'end')
              collectedEvents.push(eventData)

              const eventType =
                event.data?.params?.eventType || event.type || ''

              if (
                (eventType === 'stream_end' || eventType === 'end') &&
                currentMessageId &&
                !isStreamComplete
              ) {
                isStreamComplete = true

                if (!loaderStopped) {
                  dispatch(stopLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
                  loaderStopped = true
                }

                handleStreamCompletion(
                  currentMessageId,
                  collectedEvents,
                  getState,
                  dispatch
                )
              }
            }

            currentSSECancelFunction = null
          },
          onError: event => {
            currentSSECancelFunction = null
            throw new Error(event.message || 'Unknown error occurred')
          },
        },
        {
          typingDelay: 80,
          timeout: 300000,
          retryAttempts: 1,
        }
      )

      currentSSECancelFunction = connection.cancel

      await connection.promise
    } catch (error: any) {
      currentSSECancelFunction = null

      console.error('SSE Error:', error)

      dispatch(
        updateAppAlert({
          message: error.message || 'Failed to send prompt to AI agent',
          messageType: 'ERROR',
          autoClose: true,
        })
      )

      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      if (!loaderStopped) {
        await dispatch(stopLoading(LoadingTypes.GENERATE_AI_AGENT_LOADING))
      }
    }
  }

export const getChatHistory =
  (
    partnerId: string,
    userId: string,
    sessionId: string,
    chatNextToken: string | null
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getChats(
        partnerId,
        userId,
        sessionId,
        chatNextToken
      )
      dispatch(clearChats())
      dispatch(setActiveSessionId(sessionId))

      if (response.data.has_more) {
        dispatch(setHasMoreChats(true))
        dispatch(setChatNextToken(response.data.next_token))
      } else {
        dispatch(setHasMoreChats(false))
      }
      const sortedMessages = response.data.messages.sort(
        (a: any, b: any) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
      dispatch(setChats(camelize(sortedMessages)))
      return camelize(sortedMessages)
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      return []
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const loadMoreChatMessages =
  (partnerId: string, userId: string, sessionId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { chatNextToken, hasMoreChats } = getState().propensity

    if (!hasMoreChats || !chatNextToken) {
      return
    }

    await dispatch(startLoading(LoadingTypes.LOAD_MORE_CHATS))
    try {
      const response = await getChats(
        partnerId,
        userId,
        sessionId,
        chatNextToken
      )
      dispatch(setChatNextToken(response.data.next_token))
      dispatch(setHasMoreChats(response.data.has_more))

      const sortedMessages = response.data.messages.sort(
        (a: any, b: any) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
      dispatch(prependChats(camelize(sortedMessages)))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.LOAD_MORE_CHATS))
    }
  }

export const updateInferenceLimit =
  (partnerId: string, data: InferenceLimitUpdate) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await putInferenceLimit(partnerId, data)
      dispatch(
        updateAppAlert({
          message: DefaultSuccesrOnSubmitData,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return response
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const addSeller =
  (partnerId: string, data: SellerUrlUpdate) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await add_seller(partnerId, data)
      dispatch(
        updateAppAlert({
          message: DefaultSuccesrOnSubmitData,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return response
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const getConversations =
  (
    partnerId: string,
    userId: string,
    page: number = 1,
    pageSize: number = 15
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GET_CONVERSATIONS))
    try {
      const response = await fetchConversations(
        partnerId,
        userId,
        page,
        pageSize
      )
      const data = camelize(response.data)
      const conversations = data.conversations ?? []
      const isEmptyPage = conversations.length === 0
      const currentConversations = getState().propensity.conversations ?? []
      const effectiveTotal = calculateEffectiveTotal(
        isEmptyPage,
        page,
        data.total,
        conversations.length,
        currentConversations.length
      )

      dispatch(
        setConversationsPageInfo({
          page: data.page,
          pageSize: data.pageSize,
          total: effectiveTotal,
        })
      )

      if (page <= 1) {
        dispatch(setConversations(conversations))
      } else {
        dispatch(appendConversations(conversations))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GET_CONVERSATIONS))
    }
  }

export const fetchRecommendations =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const response = await getRecommendations(partnerId)
      const data = camelize(response.data)
      dispatch(setRecommendations(data))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const fetchSubscribed = (partnerId: string) => {
  return async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<OnboardingStatusResponse> => {
    await dispatch(startLoading(LoadingTypes.FETCH_SUBSCRIBED))
    try {
      const response = await checkOnboardingStatus(partnerId)
      return response.data as OnboardingStatusResponse
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      throw errorResponse
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_SUBSCRIBED))
    }
  }
}

export const setInferenceLimit = (data: Limit) => ({
  type: PropensityActionTypes.SET_INFERENCE_LIMIT,
  payload: data,
})
export const setSeller = (data: boolean) => ({
  type: PropensityActionTypes.SET_SELLER,
  payload: data,
})
export const setPromptInput = (data: string) => ({
  type: PropensityActionTypes.SET_PROMPT_INPUT,
  payload: data,
})
export const clearPromptInput = () => ({
  type: PropensityActionTypes.CLEAR_PROMPT_INPUT,
})

export const setHasOpenedAiDrawer = (hasOpened: boolean) => ({
  type: PropensityActionTypes.SET_HAS_OPENED_AI_DRAWER,
  payload: hasOpened,
})

export const setChats = (chats: Chat[]) => ({
  type: PropensityActionTypes.SET_CHATS,
  payload: chats,
})

export const addChat = (chat: Chat) => ({
  type: PropensityActionTypes.ADD_CHAT,
  payload: chat,
})

export const updateChat = (
  eventId: string,
  updates: Partial<Omit<Chat, 'eventId'>>
) => ({
  type: PropensityActionTypes.UPDATE_CHAT,
  payload: { eventId, updates },
})

export const setActiveSessionId = (sessionId: string) => ({
  type: PropensityActionTypes.SET_ACTIVE_SESSION_ID,
  payload: sessionId,
})

export const clearActiveSessionId = () => ({
  type: PropensityActionTypes.CLEAR_ACTIVE_SESSION_ID,
})

export const setConversations = (conversations: Conversation[]) => ({
  type: PropensityActionTypes.SET_CONVERSATIONS,
  payload: conversations,
})

export const appendConversations = (conversations: Conversation[]) => ({
  type: PropensityActionTypes.APPEND_CONVERSATIONS,
  payload: conversations,
})

export const clearConversations = () => ({
  type: PropensityActionTypes.CLEAR_CONVERSATIONS,
})

export const setConversationsPageInfo = (data: {
  page: number
  pageSize: number
  total: number
}) => ({
  type: PropensityActionTypes.SET_CONVERSATIONS_PAGE_INFO,
  payload: data,
})

export const clearChats = () => ({
  type: PropensityActionTypes.CLEAR_CHATS,
})

export const setChatNextToken = (chatNextToken: string) => ({
  type: PropensityActionTypes.SET_CHAT_NEXT_TOKEN,
  payload: chatNextToken,
})

export const clearChatNextToken = () => ({
  type: PropensityActionTypes.CLEAR_CHAT_NEXT_TOKEN,
})

export const prependChats = (chats: Chat[]) => ({
  type: PropensityActionTypes.PREPEND_CHATS,
  payload: chats,
})

export const setHasMoreChats = (hasMore: boolean) => ({
  type: PropensityActionTypes.SET_HAS_MORE_CHATS,
  payload: hasMore,
})

export const setRecommendations = (data: Recommendations | null) => ({
  type: PropensityActionTypes.SET_RECOMMENDATIONS,
  payload: data,
})

export const clearRecommendations = () => ({
  type: PropensityActionTypes.CLEAR_RECOMMENDATIONS,
})
export const setReportsData = (
  data: PropensityPayload,
  pageSize: number,
  appendRecords = false
) => ({
  type: PropensityActionTypes.SET_REPORTS_DATA,
  payload: appendRecords
    ? { ...data, pageSize, appendRecords: true as const }
    : { ...data, pageSize },
})

export const showDefault = () => ({
  type: PropensityActionTypes.SHOW_DEFAULT,
})

export const hideDefault = () => ({
  type: PropensityActionTypes.HIDE_DEFAULT,
})

export const setPropensityPageSize = (pageSize: number) => ({
  type: PropensityActionTypes.PROPENSITY_SET_LIMIT,
  payload: pageSize,
})

export const setPropensityPageNumber = (pageNumber: number) => ({
  type: PropensityActionTypes.PROPENSITY_SET_PAGE_NUMBER,
  payload: pageNumber,
})

export const clearPropensityReports = () => ({
  type: PropensityActionTypes.CLEAR_PROPENSITY_REPORTS,
})

export const setDomainUrls = (domainUrls: string[]) => ({
  type: PropensityActionTypes.SET_DOMAIN_URLS,
  payload: domainUrls,
})

export const clearDomainUrls = () => ({
  type: PropensityActionTypes.CLEAR_DOMAIN_URLS,
})

export const setFilters = (filters: FiltersState) => ({
  type: PropensityActionTypes.SET_FILTERS,
  payload: filters,
})

export const showAlert = () => ({
  type: PropensityActionTypes.SHOW_ALERT,
})

export const hideAlert = () => ({
  type: PropensityActionTypes.HIDE_ALERT,
})

export const setDropDownKey = (dropDownKey: string) => ({
  type: PropensityActionTypes.SET_DROP_DOWN_KEY,
  payload: dropDownKey,
})

export const clearDropDownKey = () => ({
  type: PropensityActionTypes.CLEAR_DROP_DOWN_KEY,
})
export const clearFilters = () => ({
  type: PropensityActionTypes.CLEAR_PROPENSITY_FILTERS,
})
export const setIsExternalLoading = (isExternalLoading: boolean) => ({
  type: PropensityActionTypes.SET_IS_EXTERNAL_LOADING,
  payload: isExternalLoading,
})
export const clearIsExternalLoading = () => ({
  type: PropensityActionTypes.CLEAR_IS_EXTERNAL_LOADING,
})

export const setPageOneForPropensity = () => async (dispatch: AppDispatch) => {
  await dispatch(setPropensityPageNumber(1))
}

export const setUserConfigurations = (data: UserConfiguration) => ({
  type: PropensityActionTypes.SET_USER_CONFIGURATIONS,
  payload: data,
})

export const setUploadedByUserMails = (userMails: string[]) => ({
  type: PropensityActionTypes.SET_UPLOADED_BY_USER_MAILS,
  payload: userMails,
})

type PlatformUsersFromPtb = Array<{
  user_mail: string
  user_name: string | null
}>

export const setPlatformUsersFromPtb = (users: PlatformUsersFromPtb) => ({
  type: PropensityActionTypes.SET_PLATFORM_USERS_FROM_PTB,
  payload: users,
})

export const fetchPlatformUsersFromPtb =
  (partnerId: string) => async (dispatch: AppDispatch) => {
    try {
      const res = await fetchUsersList(partnerId)
      const data = res?.data as
        | {
            partner_id: string
            users: PlatformUsersFromPtb
            count: number
          }
        | undefined

      dispatch(setPlatformUsersFromPtb(data?.users || []))
    } catch (error: unknown) {
      dispatch(setPlatformUsersFromPtb([]))
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch platform users list:', error)
    }
  }

export const setArchivedFlag = (archived: boolean) => ({
  type: PropensityActionTypes.SET_ARCHIVED_FLAG,
  payload: archived,
})

export const updateUserConfigurations =
  (partnerId: string, userId: string, data: Partial<UserConfiguration>) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_USER_CONFIGURATIONS))
    try {
      const payload = snakeize(data)
      const response = await putUserConfigurations(partnerId, userId, payload)
      if (response?.data) {
        const camelizedData = camelize(response.data) as UserConfiguration
        await dispatch(setUserConfigurations(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_USER_CONFIGURATIONS))
    }
  }

export const fetchAllIcpData =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_ALL_ICPS))
    try {
      const response = await fetchAllIcps(partnerId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response.data) as {
          total?: number
          icps?: (ICPData & { name?: string })[]
        }
        const mappedIcps =
          camelizedData.icps?.map(icp => ({
            ...icp,
            icpName: icp.icpName ?? icp.name ?? '',
          })) ?? null
        await dispatch(
          setAllIcpData({
            total: camelizedData.total ?? 0,
            icps: mappedIcps,
          })
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_ALL_ICPS))
    }
  }

export const updateIcpData =
  (
    partnerId: string,
    icpId: string,
    updates: { isActive?: boolean; isDefault?: boolean },
    successMessage?: string
  ) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<boolean> => {
    await dispatch(startLoading(LoadingTypes.UPDATE_ICP))
    try {
      await updateIcpState(partnerId, icpId, updates)

      dispatch(updateIcpInList(icpId, updates))

      if (updates.isDefault === true) {
        const state = getState()
        const allIcps = state.allIcpData.icps || []
        allIcps.forEach((icp: ICPData) => {
          if (icp.id !== icpId && icp.isDefault) {
            dispatch(updateIcpInList(icp.id!, { isDefault: false }))
          }
        })
      }

      dispatch(
        updateAppAlert({
          message: successMessage ?? DefaultSuccesrOnSubmitData,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return true
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )

      await dispatch(fetchAllIcpData(partnerId))
      return false
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_ICP))
    }
  }

export const fetchUserConfigurations =
  (partnerId: string, userId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_USER_CONFIGURATIONS))
    try {
      const response = await getUserConfigurations(partnerId, userId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response.data) as UserConfiguration
        await dispatch(setUserConfigurations(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_USER_CONFIGURATIONS))
    }
  }
